import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Factory, Globe2, CheckCircle2, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FACTORY_IMG = "/faytex-factory.webp";

export default function B2B() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", country: "", phone: "",
    inquiry_type: "b2b", monthly_volume: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you — we will be in touch within 2 business days.");
      setDone(true);
    } catch {
      toast.error("Could not send your inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="b2b-page" className="bg-white">
      <section className="relative isolate overflow-hidden bg-faytex-ink text-white">
        <div className="absolute inset-0 -z-10 opacity-40">
          <img src={FACTORY_IMG} alt="Faytex factory" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-40">
          <p className="text-xs uppercase tracking-widest text-faytex-pink">Contract Manufacturing</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl mt-3 tracking-tighter max-w-4xl">
            Manufactured in Ghana. Trusted worldwide.
          </h1>
          <p className="mt-6 max-w-2xl text-white/80 text-lg">
            Build your private label sanitary pad line with West Africa&apos;s most modern factory. Full-service
            R&D, packaging, FDA-grade production and export logistics.
          </p>
        </div>
      </section>

      {/* Capabilities */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-3 gap-6">
        {[
          { icon: <Factory className="w-5 h-5" />, t: "High-Capacity", d: "12M+ units / month across regular, maternity & active lines." },
          { icon: <CheckCircle2 className="w-5 h-5" />, t: "Clinically Trusted", d: "Dermatologically tested, ISO-compliant manufacturing." },
          { icon: <Globe2 className="w-5 h-5" />, t: "Global Logistics", d: "FOB Tema port, EU & US-ready packaging, exporting to 8 markets." },
        ].map((c) => (
          <div key={c.t} className="rounded-2xl border border-gray-100 p-8 bg-white shadow-soft hover:shadow-hover transition-shadow">
            <div className="w-10 h-10 rounded-full bg-faytex-pinkLight text-faytex-pink flex items-center justify-center">{c.icon}</div>
            <h3 className="font-heading text-2xl mt-4">{c.t}</h3>
            <p className="text-sm text-faytex-muted mt-2 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </section>

      {/* Form */}
      <section className="bg-faytex-pinkLight/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Request a partnership</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">Let&apos;s build your line.</h2>
            <p className="mt-4 text-faytex-muted">Tell us about your brand and we will respond within 2 business days.</p>
          </div>

          {done ? (
            <div data-testid="b2b-success" className="mt-12 bg-white rounded-3xl shadow-hover p-10 text-center">
              <div className="w-14 h-14 rounded-full bg-faytex-mint/20 text-emerald-600 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h3 className="font-heading text-3xl mt-5">Inquiry received.</h3>
              <p className="text-faytex-muted mt-2">Our partnerships team will reach out to {form.email}.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="b2b-form" className="mt-12 bg-white rounded-3xl shadow-hover p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Your name *" testid="b2b-name" value={form.name} onChange={(v) => update("name", v)} />
              <Field label="Email *" type="email" testid="b2b-email" value={form.email} onChange={(v) => update("email", v)} />
              <Field label="Company" testid="b2b-company" value={form.company} onChange={(v) => update("company", v)} />
              <Field label="Country" testid="b2b-country" value={form.country} onChange={(v) => update("country", v)} />
              <Field label="Phone" testid="b2b-phone" value={form.phone} onChange={(v) => update("phone", v)} />
              <div>
                <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Estimated monthly volume</label>
                <select
                  data-testid="b2b-volume"
                  value={form.monthly_volume}
                  onChange={(e) => update("monthly_volume", e.target.value)}
                  className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
                >
                  <option value="">Select…</option>
                  <option value="<100k">Less than 100,000 units</option>
                  <option value="100k-500k">100,000 – 500,000 units</option>
                  <option value="500k-1m">500,000 – 1,000,000 units</option>
                  <option value="1m+">1,000,000+ units</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">Tell us about your brand *</label>
                <textarea
                  data-testid="b2b-message"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  rows={5}
                  className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
                  placeholder="Brand vision, target markets, packaging needs…"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button
                  type="submit"
                  data-testid="b2b-submit-form"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover text-white px-7 py-3.5 text-sm font-semibold transition-colors disabled:opacity-60"
                >
                  {submitting ? "Sending…" : (<>Send inquiry <ArrowRight className="w-4 h-4" /></>)}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-faytex-muted font-semibold">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-gray-50 border border-gray-200 focus:border-faytex-pink focus:ring-faytex-pink rounded-xl px-4 py-3 text-sm outline-none"
      />
    </div>
  );
}
